import styles from "./MapResults.module.scss";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import MapResultItem from "../MapResultItem/MapResultItem";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Icon from "../Icon";
import { TSection } from "../../model/type/api/TSection";
import ParkingSessionItem from "../ParkingSessionItem/ParkingSessionItem";
import useParkingSession from "../../model/useParkingSession";
import useFavoriteZone from "../../model/useFavoriteZone";
import { PARK_MACHINE_TYPE } from "../../constants";
import { ParkingSessionService } from "../../services/parkingSessionService";
import { useParkingSessionCleanup } from "../../hooks/useParkingSessionCleanup";

type PropsItems = {
    onSelect?: (section: TSection) => void;
    forceReload?: () => void;
} & PropsItemsShared;

type PropsItemsShared = {
    wrapperClassName?: string;
    snapIndex?: number;
    onChangeScroll?: (y: number) => void;
    raLevels: any;
    zones?: TSection[];
    filteredResults?: TSection[];
    hideResults: boolean;
    currentSectionCode?: string;
    version?: number;
};

type PropsItem = {
    type: string;
    items?: TSection[];
    onSelect?: (section: TSection) => void;
    forceReload: () => void;
} & PropsItemsShared;

const MapResultItems: React.FC<PropsItem> = ({ onSelect, items = [], type, forceReload, ...props }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    let _styles = {};

    const heightOfList = () => {
        const fullHeight = window.innerHeight;

        return {
            0: `calc(${fullHeight}px - 225px)`,
            1: `calc(${fullHeight}px)`,
        }[props.snapIndex];
    };

    if (props.snapIndex !== undefined) {
        _styles = { height: heightOfList(), overflowY: props.snapIndex === 1 ? "hidden" : "scroll" };
    }

    useEffect(() => {
        const element = ref.current;
        const onScroll = (e: any) => {
            props.onChangeScroll && props.onChangeScroll(e.target.scrollTop);
        };

        element?.addEventListener("scroll", onScroll);

        return () => {
            element?.removeEventListener("scroll", onScroll);
        };
    }, [props.onChangeScroll]);

    const sortedItems = useMemo(() => {
        if (props.hideResults) return [];

        return [...items].sort((a, b) => {
            if ((a.featureType === "parkingZone" ? a.sectionID : a.parkMachineID) === props.currentSectionCode) return -1;
            if ((b.featureType === "parkingZone" ? b.sectionID : b.parkMachineID) === props.currentSectionCode) return 1;
            return 0;
        });
    }, [props.hideResults, items, props.currentSectionCode]);

    const { parkingSession } = useParkingSession();
    const { zones } = useFavoriteZone();

    const visibleSessions = useMemo(() => {
        return ParkingSessionService.filterVisibleSessions(parkingSession);
    }, [parkingSession]);

    const getPlaceName = useCallback(
        (section: TSection) => {
            return section.featureType === "parkingZone"
                ? (section.streetName ?? section.sectionCode)
                : (section.parkMachineType === PARK_MACHINE_TYPE.infobox
                    ? `${t("home.MapSearchComponent.zones.infoBox")}: `
                    : `${t("home.MapSearchComponent.zones.parkingMachine")}: `) +
                (section.streetName ?? section.parkMachineCode);
        },
        [t]
    );

    const parsedZones = useMemo(() => {
        return zones
            .map((zone) => {
                if (zone?.json === undefined) return null;
                try {
                    const section = JSON.parse(zone.json);
                    if (!section.featureType) return null;
                    return { zone, section };
                } catch (e) {
                    return null;
                }
            })
            .filter((item): item is { zone: any; section: TSection } => item !== null);
    }, [zones]);

    return (
        <div className={styles.items} style={_styles} ref={ref}>
            {visibleSessions.map((session) => (
                <ParkingSessionItem session={session} key={session.guid} />
            ))}
            {parsedZones.map(({ zone, section }) => {
                const sectionId = section.featureType === "parkingZone" ? section.sectionID : section.parkMachineID;
                const textRaLevels = props.raLevels[section.sectionID] ? props.raLevels[section.sectionID] + " - " : "";
                const parkingInfo = `${section.streetName ? (section.featureType === "parkingZone" ? section.sectionCode : section.parkMachineCode) + " - " : ""}${textRaLevels} max ${section.tariffInfo.maxHour} h`;

                return (
                    <MapResultItem
                        key={`${sectionId}-${props.version}`}
                        section={section}
                        sectionId={sectionId}
                        placeName={getPlaceName(section)}
                        parkingInfo={parkingInfo}
                        isCurrent={props.currentSectionCode === sectionId}
                        featureType={section.featureType}
                        onClick={() => {
                            onSelect && onSelect(section);
                        }}
                        forceReload={forceReload}
                    />
                );
            })}
            {sortedItems.map((section) => {
                const sectionId = section.featureType === "parkingZone" ? section.sectionID : section.parkMachineID;
                const textRaLevels = props.raLevels[section.sectionID] ? props.raLevels[section.sectionID] + " - " : "";
                const parkingInfo = `${section.streetName ? (section.featureType === "parkingZone" ? section.sectionCode : section.parkMachineCode) + " - " : ""}${textRaLevels}  max ${section.tariffInfo.maxHour} h`;

                return (
                    <MapResultItem
                        key={`${sectionId}`}
                        section={section}
                        sectionId={sectionId}
                        placeName={getPlaceName(section)}
                        parkingInfo={parkingInfo}
                        isCurrent={props.currentSectionCode === sectionId}
                        featureType={section.featureType}
                        onClick={() => {
                            onSelect && onSelect(section);
                        }}
                        favorites={false}
                        forceReload={forceReload}
                    />
                );
            })}
            {sortedItems.length === 0 && zones.length === 0 && <div>{t("home.MapResultsComponent.noResults")}</div>}
        </div>
    );
};

const MapResults: React.FC<PropsItems> = ({ onSelect = null, ...props }) => {
    const { t } = useTranslation();
    const isLogged = false;

    useParkingSessionCleanup({ runOnMount: true, intervalMinutes: null });

    return (
        <div className={styles.mapResultsWrapper}>
            <div className={`${styles.mapResultsContainer} ${props.wrapperClassName}`}>
                {isLogged && (
                    <Tabs selectedTabClassName={styles.selected} onChange={() => props.onChangeScroll && props.onChangeScroll(0)}>
                        <TabList className={styles.tabList}>
                            <Tab className={styles.tabItem}>
                                <Icon.Location className={styles.tabIcon} size={14} />
                                {t("home.MapResultsComponent.tab.nearest")}
                            </Tab>
                            <Tab className={styles.tabItem}>
                                <Icon.Hearth className={styles.tabIcon} size={14} />
                                {t("home.MapResultsComponent.tab.favourite")}
                            </Tab>
                            <Tab className={styles.tabItem}>
                                <Icon.History className={styles.tabIcon} size={14} />
                                {t("home.MapResultsComponent.tab.history")}
                            </Tab>
                        </TabList>

                        <TabPanel>
                            <MapResultItems
                                key={`nearest-${props.version}`}
                                type={"nearest"}
                                forceReload={props.forceReload}
                                {...props}
                            />
                        </TabPanel>
                        <TabPanel>
                            <MapResultItems
                                key={`favourite-${props.version}`}
                                type={"favourite"}
                                forceReload={props.forceReload}
                                {...props}
                            />
                        </TabPanel>
                        <TabPanel>
                            <MapResultItems
                                key={`history-${props.version}`}
                                type={"history"}
                                forceReload={props.forceReload}
                                {...props}
                            />
                        </TabPanel>
                    </Tabs>
                )}

                {!isLogged && (
                    <MapResultItems
                        key={`filtered-${props.version}`}
                        items={props.filteredResults}
                        type={"filtered"}
                        onSelect={onSelect}
                        forceReload={props.forceReload}
                        {...props}
                    />
                )}
            </div>
        </div>
    );
};

export default MapResults;
